import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Mail, Users, AlertTriangle, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aidwaive_counselor_token";

/**
 * Read-only counselor overview.
 *
 * Auth model: a single static Bearer token, supplied by the AidWaive
 * operator out-of-band (Slack, secure email, never in app). The token is
 * stored only in this tab's sessionStorage, never persisted across sessions.
 *
 * This page exposes ZERO PII and ZERO financial data, it shows aggregate
 * counts only, so a counselor can see "do families in my state still need
 * to file?" at a glance.
 */
export default function CounselorPage() {
  const [tokenInput, setTokenInput] = useState("");
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY) || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = async (tk) => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`${API}/counselor/overview`, {
        headers: { Authorization: `Bearer ${tk}` },
      });
      if (r.status === 401) {
        setError("That token isn't valid. Double-check it with whoever shared it with you.");
        sessionStorage.removeItem(TOKEN_KEY);
        setToken("");
        return;
      }
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setError(j.detail || `Server returned ${r.status}.`);
        return;
      }
      const j = await r.json();
      setData(j);
    } catch (e) {
      setError("Couldn't reach the server. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (token) load(token); }, [token]);

  const onSubmit = (e) => {
    e.preventDefault();
    const t = tokenInput.trim();
    if (!t) return;
    sessionStorage.setItem(TOKEN_KEY, t);
    setToken(t);
  };

  const signOut = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setToken("");
    setData(null);
  };

  if (!token) {
    return (
      <Shell>
        <div className="card max-w-md mx-auto" data-testid="counselor-token-card">
          <div className="flex items-start gap-3 mb-5">
            <ShieldCheck className="w-5 h-5 text-blue-dim mt-0.5" strokeWidth={1.5} />
            <div>
              <h2 className="font-heading text-2xl text-ink">Counselor access</h2>
              <p className="text-sm text-ink-muted mt-1">
                Read-only aggregate stats, no PII, no financial data. Paste the token shared with you to continue.
              </p>
            </div>
          </div>
          <form onSubmit={onSubmit} className="space-y-3">
            <input
              type="password"
              autoComplete="off"
              required
              placeholder="Paste counselor token"
              value={tokenInput}
              onChange={e => setTokenInput(e.target.value)}
              className="input w-full"
              data-testid="counselor-token-input"
            />
            <button type="submit" className="btn btn-primary w-full" data-testid="counselor-token-submit">
              Continue
            </button>
            {error && (
              <p className="text-sm text-danger flex items-start gap-2" data-testid="counselor-token-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                {error}
              </p>
            )}
          </form>
          <p className="text-[11px] text-ink-dim mt-5 leading-relaxed">
            The token is stored only in this browser tab. Closing the tab signs you out. There's no login,
            no email recovery, no password, by design.
          </p>
        </div>
      </Shell>
    );
  }

  if (loading && !data) {
    return (
      <Shell>
        <div className="card text-ink-muted text-sm" data-testid="counselor-loading">Loading aggregate stats…</div>
      </Shell>
    );
  }

  if (error) {
    return (
      <Shell signOut={signOut}>
        <div className="card border-danger flex items-start gap-3" data-testid="counselor-error">
          <AlertTriangle className="w-5 h-5 text-danger mt-0.5" />
          <div>
            <div className="font-medium text-ink">Couldn't load overview</div>
            <p className="text-sm text-ink-muted mt-1">{error}</p>
            <button onClick={() => load(token)} className="btn btn-ghost mt-4 text-xs">
              <RefreshCw className="w-3.5 h-3.5" /> Retry
            </button>
          </div>
        </div>
      </Shell>
    );
  }

  if (!data) return null;

  const subs = data.subscribers || {};
  const dlines = data.deadlines || {};
  const emails = data.emails || {};
  const generated = data.generatedAt ? new Date(data.generatedAt) : null;

  return (
    <Shell signOut={signOut}>
      <div className="mb-8 flex items-baseline justify-between flex-wrap gap-3">
        <p className="text-xs text-ink-dim" data-testid="counselor-generated-at">
          Generated {generated ? generated.toLocaleString() : ","}
        </p>
        <button onClick={() => load(token)} className="text-xs text-ink-muted hover:text-ink inline-flex items-center gap-1.5"
          data-testid="counselor-refresh">
          <RefreshCw className="w-3 h-3" /> Refresh
        </button>
      </div>

      {/* KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-10" data-testid="counselor-kpis">
        <Kpi icon={Users}  label="Total subscribers"        value={subs.totalConfirmed} />
        <Kpi icon={Users}  label="Awaiting confirmation"    value={subs.totalPending} muted />
        <Kpi icon={Mail}   label="Emails sent (90 days)"    value={emails.totalLast90Days} />
        <Kpi icon={AlertTriangle} label="Within 7 days"      value={dlines.subscribersWithin7Days} accent="urgent" />
      </div>

      {/* Deadlines panel */}
      <section className="mb-10">
        <h2 className="font-heading text-2xl text-ink mb-4">Subscribers by deadline urgency</h2>
        <div className="card">
          <UrgencyRow label="Within 7 days"        n={dlines.subscribersWithin7Days}  variant="urgent" />
          <UrgencyRow label="Within 14 days"       n={dlines.subscribersWithin14Days} variant="urgent" />
          <UrgencyRow label="Within 30 days"       n={dlines.subscribersWithin30Days} variant="warning" />
          <UrgencyRow label="Past deadline"        n={dlines.subscribersPastDeadline} variant="passed" last />
        </div>
      </section>

      {/* By state */}
      <section className="mb-10">
        <h2 className="font-heading text-2xl text-ink mb-4">Subscribers by state</h2>
        {(subs.byState || []).length === 0 ? (
          <div className="card text-ink-muted text-sm" data-testid="counselor-empty-states">
            No confirmed subscribers yet.
          </div>
        ) : (
          <div className="card-tight card overflow-x-auto">
            <table className="w-full text-sm" data-testid="counselor-state-table">
              <thead>
                <tr className="text-left text-ink-dim border-b border-border">
                  <th className="py-2.5 pr-4 font-medium">State</th>
                  <th className="py-2.5 pr-4 font-medium">Subscribers</th>
                  <th className="py-2.5 pr-4 font-medium">Deadline</th>
                  <th className="py-2.5 font-medium">Days away</th>
                </tr>
              </thead>
              <tbody>
                {subs.byState.map(row => (
                  <tr key={row.state} className="border-b border-border last:border-0">
                    <td className="py-2.5 pr-4 text-ink">{row.stateName}</td>
                    <td className="py-2.5 pr-4 text-ink">{row.count}</td>
                    <td className="py-2.5 pr-4 text-ink-muted">{row.deadline || ","}</td>
                    <td className={`py-2.5 ${urgencyClass(row.daysUntilDeadline)}`}>
                      {row.daysUntilDeadline === null || row.daysUntilDeadline === undefined
                        ? ","
                        : row.daysUntilDeadline < 0
                          ? `${Math.abs(row.daysUntilDeadline)} ago`
                          : `${row.daysUntilDeadline} day${row.daysUntilDeadline === 1 ? "" : "s"}`}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Emails by milestone */}
      <section className="mb-10">
        <h2 className="font-heading text-2xl text-ink mb-4">Reminder emails sent (last 90 days)</h2>
        {(emails.byMilestone || []).length === 0 ? (
          <div className="card text-ink-muted text-sm">No reminder emails sent in the last 90 days.</div>
        ) : (
          <div className="card-tight card">
            <table className="w-full text-sm">
              <tbody>
                {emails.byMilestone.map(m => (
                  <tr key={m.milestone} className="border-b border-border last:border-0">
                    <td className="py-2.5 pr-4 text-ink-muted">{m.milestone.replace(/_/g, " ")}</td>
                    <td className="py-2.5 text-right text-ink">{m.count}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <p className="text-xs text-ink-dim leading-relaxed max-w-xl" data-testid="counselor-disclaimer">
        {data.note}
      </p>
    </Shell>
  );
}

function Shell({ children, signOut }) {
  return (
    <div className="theme-light min-h-screen flex flex-col bg-bg">
      <main className="flex-1">
        <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-8 sm:py-12">
          <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
            <div>
              <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-ink-muted hover:text-ink mb-6"
                data-testid="counselor-back">
                <ArrowLeft className="w-3.5 h-3.5" /> Back to AidWaive
              </Link>
              <div className="text-[11px] uppercase tracking-[0.18em] text-ink-dim mb-2">Counselor view</div>
              <h1 className="font-heading text-3xl sm:text-4xl text-ink leading-[1.1] mb-2">
                Aggregate overview.
              </h1>
              <p className="text-ink-muted text-sm leading-relaxed max-w-xl">
                Read-only. No PII or financial data. Useful for spotting which states have urgent FAFSA deadlines.
              </p>
            </div>
            {signOut && (
              <button onClick={signOut} className="btn btn-ghost text-xs" data-testid="counselor-signout">
                Sign out
              </button>
            )}
          </div>
          {children}
        </div>
      </main>
      <footer className="border-t border-border mt-16">
        <div className="max-w-4xl mx-auto w-full px-4 sm:px-6 py-8 text-xs text-ink-dim flex justify-between">
          <Link to="/" className="hover:text-ink">← All AidWaive tools</Link>
          <span>Read-only · token in session storage</span>
        </div>
      </footer>
    </div>
  );
}

function Kpi({ icon: Icon, label, value, accent, muted }) {
  const colorClass = accent === "urgent" ? "text-danger" : muted ? "text-ink-muted" : "text-ink";
  return (
    <div className="card" data-testid={`counselor-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <Icon className="w-4 h-4 text-ink-dim mb-3" strokeWidth={1.5} />
      <div className={`font-heading text-3xl ${colorClass}`}>{value ?? 0}</div>
      <div className="text-xs text-ink-dim mt-2">{label}</div>
    </div>
  );
}

function UrgencyRow({ label, n, variant, last }) {
  const colorMap = { urgent: "text-danger", warning: "text-amber", passed: "text-ink-dim" };
  return (
    <div className={`flex items-center justify-between py-2.5 ${last ? "" : "border-b border-border"}`}>
      <span className="text-sm text-ink-muted">{label}</span>
      <span className={`font-heading text-2xl ${colorMap[variant] || "text-ink"}`}>{n ?? 0}</span>
    </div>
  );
}

function urgencyClass(days) {
  if (days === null || days === undefined) return "text-ink-dim";
  if (days < 0)  return "text-ink-dim";
  if (days <= 7) return "text-danger";
  if (days <= 30) return "text-amber";
  return "text-ink";
}
